import { Component, DestroyRef, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { NbToastrService } from '@nebular/theme';
import { forkJoin } from 'rxjs';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { ConfirmationData, ConfirmationDialogComponent } from '../../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component';
import { FarmNote, FarmNoteType } from '../../../models/farm-note';
import { lookupFarmNoteIcon } from '../../../models/farm-note-icons';
import { FarmNoteService } from '../../../services/farm-note.service';
import { FarmNoteTypeService } from '../../../services/farm-note-type.service';
import { FarmNoteTypesDialogComponent } from '../farm-note-types-dialog/farm-note-types-dialog.component';

interface EditableNote extends FarmNote {
  _editing?: boolean;
  _draftNote?: string;
  _draftType?: string;
}

const FALLBACK_COLOR = '#8f9bb3';

@Component({
  selector: 'kw-farm-notes',
  templateUrl: './farm-notes.component.html',
  styleUrls: ['./farm-notes.component.scss']
})
export class FarmNotesComponent implements OnChanges {
  @Input() farmId: number;
  @Input() canEdit = false;

  private destroyRef = inject(DestroyRef);

  public notes: EditableNote[] = [];
  public types: FarmNoteType[] = [];
  public isLoading = false;
  public isAdding = false;

  public newType = '';
  public newNote = '';

  public faPen = faPen;
  public faTrash = faTrash;
  public icon = lookupFarmNoteIcon;

  constructor(
    private noteService: FarmNoteService,
    private typeService: FarmNoteTypeService,
    private dialog: MatDialog,
    private toastr: NbToastrService,
    private errorService: ErrorService,
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.farmId && this.farmId) {
      this.load();
    }
  }

  private load() {
    this.isLoading = true;
    forkJoin([this.noteService.getByFarm(this.farmId), this.typeService.list()])
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: ([notes, types]) => {
          this.types = types || [];
          this.notes = (notes || []).map(n => this.normalize(n));
          if (!this.types.some(t => t.name === this.newType)) {
            this.newType = this.types[0]?.name || '';
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          this.errorService.handleError(err);
        }
      });
  }

  /** The API sends UTC timestamps without a zone marker; tag them so they display in local time. */
  private normalize(n: FarmNote): EditableNote {
    const asUtc = (v?: string) => v && !/[zZ]|[+-]\d\d:?\d\d$/.test(v) ? v + 'Z' : v;
    return { ...n, timeCreated: asUtc(n.timeCreated), timeModified: asUtc(n.timeModified) };
  }

  typeFor(name: string): FarmNoteType | undefined {
    return this.types.find(t => t.name === name);
  }

  // Types that were later removed still render, just neutrally.
  colorFor(name: string): string {
    return this.typeFor(name)?.color || FALLBACK_COLOR;
  }

  iconNameFor(name: string): string {
    return this.typeFor(name)?.icon;
  }

  wasEdited(n: FarmNote): boolean {
    return !!n.timeModified && !!n.timeCreated &&
      new Date(n.timeModified).getTime() - new Date(n.timeCreated).getTime() > 60_000;
  }

  /** Types offered while editing: the active ones, plus the note's current type if since removed. */
  editTypesFor(n: EditableNote): string[] {
    const names = this.types.map(t => t.name);
    return n.type && !names.includes(n.type) ? [n.type, ...names] : names;
  }

  // ---- Add ----------------------------------------------------------------

  addNote() {
    const text = this.newNote.trim();
    if (!text || this.isAdding) return;
    this.isAdding = true;
    this.noteService.create({ farmId: this.farmId, type: this.newType, note: text })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (saved) => {
          this.notes = [this.normalize(saved), ...this.notes];
          this.newNote = '';
          this.isAdding = false;
        },
        error: (err) => {
          this.isAdding = false;
          this.errorService.handleError(err);
        }
      });
  }

  onComposerKey(event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      this.addNote();
    }
  }

  // ---- Edit ---------------------------------------------------------------

  startEdit(n: EditableNote) {
    n._draftNote = n.note;
    n._draftType = n.type;
    n._editing = true;
  }

  cancelEdit(n: EditableNote) {
    n._editing = false;
  }

  saveEdit(n: EditableNote) {
    const text = (n._draftNote || '').trim();
    if (!text) return;
    const { _editing, _draftNote, _draftType, ...payload } = n;
    this.noteService.update({ ...payload, note: text, type: _draftType })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (saved) => {
          this.notes = this.notes.map(x => x.id === saved.id ? this.normalize(saved) : x);
        },
        error: (err) => this.errorService.handleError(err)
      });
  }

  // ---- Delete -------------------------------------------------------------

  confirmDelete(n: EditableNote) {
    const data: ConfirmationData = {
      title: 'Delete Note',
      confirmMessage: 'Delete this note? This cannot be undone.',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      displayComment: false,
      isWarning: false,
    };
    this.dialog.open(ConfirmationDialogComponent, { width: '400px', data })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
        if (result !== true) return;
        // Remove optimistically; put it back if the delete fails.
        const index = this.notes.indexOf(n);
        this.notes = this.notes.filter(x => x !== n);
        this.noteService.delete(n.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          error: (err) => {
            this.notes = [...this.notes.slice(0, index), n, ...this.notes.slice(index)];
            this.errorService.handleError(err);
          }
        });
      });
  }

  // ---- Types --------------------------------------------------------------

  manageTypes() {
    this.dialog.open(FarmNoteTypesDialogComponent, { width: '640px' })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(changed => {
        if (changed) {
          this.typeService.list().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(types => {
            this.types = types || [];
            if (!this.types.some(t => t.name === this.newType)) {
              this.newType = this.types[0]?.name || '';
            }
          });
        }
      });
  }
}
